import React, { useCallback, useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";

export default function Gallery({ images = [], title, columns = 4 }) {
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);

  const move = useCallback(
    (d) => setIdx((p) => (p + d + images.length) % images.length),
    [images.length]
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
      if (e.key === "ArrowRight") move(1);
      if (e.key === "ArrowLeft") move(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, move]);

  if (!images.length) return null;

  const colClass =
    columns === 3
      ? "grid-cols-2 sm:grid-cols-3"
      : columns === 5
      ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5"
      : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4";

  return (
    <section className="bg-[#f7f7f7] py-14">
      <div className="mr-container-wide">
        {title && (
          <h2 className="mr-h2 reveal mb-8 text-center text-2xl sm:text-3xl">{title}</h2>
        )}
        <div className={`grid gap-3 ${colClass}`}>
          {images.map((src, i) => (
            <button
              key={src + i}
              onClick={() => {
                setIdx(i);
                setOpen(true);
              }}
              className="group reveal relative aspect-square overflow-hidden bg-white shadow-sm ring-1 ring-black/5"
              aria-label={`Open project photo ${i + 1}`}
            >
              <img
                src={src}
                alt={`Mr. Resurface project photo ${i + 1}`}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
              />
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-[#0c2a66]/0 opacity-0 transition-all duration-300 group-hover:bg-[#0c2a66]/45 group-hover:opacity-100">
                <ZoomIn className="h-8 w-8 text-white" />
              </span>
            </button>
          ))}
        </div>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4"
          onClick={() => setOpen(false)}
        >
          <button
            onClick={() => setOpen(false)}
            aria-label="Close"
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/25"
          >
            <X className="h-6 w-6" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              move(-1);
            }}
            aria-label="Previous photo"
            className="absolute left-3 rounded-full bg-white/10 p-3 text-white transition-colors hover:bg-white/25 sm:left-8"
          >
            <ChevronLeft className="h-7 w-7" />
          </button>
          <img
            src={images[idx]}
            alt={`Mr. Resurface project photo ${idx + 1}`}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[86vh] max-w-[90vw] object-contain shadow-2xl"
          />
          <button
            onClick={(e) => {
              e.stopPropagation();
              move(1);
            }}
            aria-label="Next photo"
            className="absolute right-3 rounded-full bg-white/10 p-3 text-white transition-colors hover:bg-white/25 sm:right-8"
          >
            <ChevronRight className="h-7 w-7" />
          </button>
          <span className="absolute bottom-5 left-1/2 -translate-x-1/2 text-sm font-semibold text-white/80">
            {idx + 1} / {images.length}
          </span>
        </div>
      )}
    </section>
  );
}
